import {
  Box, Button, Grid, Stack,
} from '@mui/material';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { v4 as uuid } from 'uuid';
import BlockProps from '../../../models/BlockProps';
import CloseButton from '../../Buttons/Close';
import BlockTableData, { BlockTableVariant } from './BlockTableData';
import TableItem from './components/TableItem';
import BlockTableItemData from './BlockTableItemData';
import Utils from '../../../utils/Utils';
import EditableText from '../../Form/EditableText';
import MoveButtons, { MoveButtonDirection } from '../../Buttons/Move';
import { useGetBase64Image } from '../../../hooks/UseGetBase64Image';
import BlockComponentImage from '../../../models/BlockComponentValues/BlockComponentImage';
import useGetBackground from '../../../hooks/UseGetBackground';
import BackGroundImageModal from '../../Modals/ImageModal/BackGroundImageModal';
import useParallaxScript from '../../../hooks/useParallaxScript';
import config from '../../../config';

const BlockTable = ({
  data, isEditing, isTemplate, editHandler, showImageModal, onCloseImageModal,
}: BlockProps) => {
  const [props, setProps] = useState<BlockTableData>(data.data as BlockTableData);

  useEffect(() => {
    setProps(data.data as BlockTableData);
  }, [data.data]);

  const handleMoveItem = useCallback((direction: MoveButtonDirection, index: number) => {
    if (direction === MoveButtonDirection.Up) {
      props.items = Utils.moveArrayItem(props.items, index, index - 1);
      editHandler(data.id, props);
    } else {
      props.items = Utils.moveArrayItem(props.items, index, index + 1);
      editHandler(data.id, props);
    }
  }, [data.id, editHandler, props]);

  const tableHeader = useMemo(() => {
    if (props.headerItem) {
      return (
        <Grid
          item
          container
          sx={{
            position: 'relative',
            height: '100%',
          }}
        >
          <CloseButton
            show={isEditing}
            updateHandler={() => {
              props.headerItem = undefined;
              editHandler(data.id, props);
            }}
          />
          <TableItem
            isHeader
            item={props.headerItem}
            isEditing={isEditing}
            isTemplate={isTemplate}
            variant={props.variant}
            handleUpdateTitle={(text: string) => {
              props.headerItem.title.value = text;
              editHandler(data.id, props);
            }}
            handleUpdateText={(text: string) => {
              props.headerItem.text.value = text;
              editHandler(data.id, props);
            }}
          />
        </Grid>
      );
    }
    return [];
  }, [data.id, editHandler, isEditing, isTemplate, props]);

  const tableItems = useMemo(() => {
    if (props.items && props.items.length > 0) {
      return props.items.map((item, index) => (
        <Grid
          key={item.id}
          item
          container
          sx={{
            position: 'relative',
            height: '100%',
          }}
        >
          <MoveButtons
            show={isEditing}
            updateHandler={(direction: MoveButtonDirection) => {
              handleMoveItem(direction, index);
            }}
            hideUp={index === 0}
            hideDown={index === props.items.length - 1}
          />
          <CloseButton
            show={isEditing}
            updateHandler={() => {
              props.items.splice(index, 1);
              editHandler(data.id, props);
            }}
          />
          <TableItem
            item={item}
            isEditing={isEditing}
            isTemplate={isTemplate}
            isFirst={index === 0}
            variant={props.variant}
            handleUpdateTitle={(text: string) => {
              props.items[index].title.value = text;
              editHandler(data.id, props);
            }}
            handleUpdateText={(text: string) => {
              props.items[index].text.value = text;
              editHandler(data.id, props);
            }}
          />
        </Grid>
      ));
    }
    return [];
  }, [data.id, editHandler, handleMoveItem, isEditing, isTemplate, props]);

  const base64Image = useGetBase64Image(props.url?.value);

  const elementUuid = uuid();
  useParallaxScript(props.url?.settings?.parallaxBool, elementUuid, config.backgroundImage.parallaxEffect);

  return (
    <Grid
      id={elementUuid}
      container
      spacing={0}
      direction="column"
      alignItems="center"
      justifyContent="center"
      sx={{
        minHeight: '120px',
        overflow: 'hidden',
        p: isEditing || isTemplate ? 1.5 : 0,
        ...useGetBackground(
          props.url?.solidColor,
          props.url?.value,
          props.url?.settings?.scale,
          props.url?.settings?.scaleBool,
        ),
        backgroundPosition: 'center 0px',
      }}
    >
      {(isEditing && !isTemplate) && (
        <BackGroundImageModal
          backgroundImage={props.url}
          isShowModal={showImageModal}
          onCloseModal={onCloseImageModal}
          handleOnConfirm={(url: BlockComponentImage) => {
            const tempBg = { ...props, url: { ...url } };
            setProps(tempBg);
            editHandler(data.id, tempBg);
          }}
        />
      )}
      <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
        className="block-content-wrapper"
      >
        {props.title.value && (
          <Grid item alignItems="center" xs={12} sx={{ minHeight: '46px', marginBottom: 2 }}>
            <Box sx={Utils.hoverBorder(isEditing)}>
              <EditableText
                text={props.title.value}
                textColor={props.title.settings?.textColor}
                isEditable={isEditing && !isTemplate}
                updateHandler={(value) => {
                  props.title.value = value;
                  editHandler(data.id, props);
                }}
              />
              <CloseButton
                show={isEditing}
                updateHandler={() => {
                  props.title.value = undefined;
                  editHandler(data.id, props);
                }}
              />
            </Box>
          </Grid>
        )}
        {props.variant === BlockTableVariant.Default && (
          <>
            {tableHeader}
          </>
        )}
        {tableItems}
      </Grid>
      {isEditing && (
        <Stack spacing={0} sx={{ border: '1px dashed lightGray', marginTop: '12px', width: 'calc(100% - 24px)' }}>
          <Button
            color="primary"
            onClick={() => {
              props.items.push(BlockTableItemData.getDefault());
              editHandler(data.id, props);
            }}
          >
            <AddCircleRoundedIcon />
          </Button>
        </Stack>
      )}
    </Grid>
  );
};

export default BlockTable;
